import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Mail, MapPin, Phone, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { contactAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      await contactAPI.send(formData);
      toast.success('Mensagem enviada com sucesso!', {
        description: 'Entraremos em contato em breve.'
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <section className="py-8 md:py-12 border-b border-[#D6D2C4]" data-testid="contact-header">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-[#5C5C5C] font-['Inter'] mb-4">
            <Link to="/" className="hover:text-[#0F5A34]">Início</Link>
            <ChevronRight size={14} />
            <span className="text-[#1A1A1A]">Contato</span>
          </div>
          
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
            Entre em Contato
          </h1>
          
          <p className="text-[#5C5C5C] font-['Inter'] text-base max-w-2xl">
            Tem alguma dúvida, sugestão ou proposta de parceria? Entre em contato conosco!
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16" data-testid="contact-content">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-4">
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-sm border border-[#D6D2C4]">
                  <div className="w-12 h-12 rounded-full bg-[#0F5A34]/10 flex items-center justify-center mb-4">
                    <Mail size={24} className="text-[#0F5A34]" />
                  </div>
                  <h3 className="font-['Montserrat'] text-sm font-semibold uppercase tracking-wider text-[#1A1A1A] mb-2">
                    Email
                  </h3>
                  <a
                    href="mailto:contato@portugalavista.com"
                    className="text-[#0F5A34] font-['Inter'] text-sm hover:underline"
                  >
                    contato@portugalavista.com
                  </a>
                </div>

                <div className="bg-white p-6 rounded-sm border border-[#D6D2C4]">
                  <div className="w-12 h-12 rounded-full bg-[#0F5A34]/10 flex items-center justify-center mb-4">
                    <MapPin size={24} className="text-[#0F5A34]" />
                  </div>
                  <h3 className="font-['Montserrat'] text-sm font-semibold uppercase tracking-wider text-[#1A1A1A] mb-2">
                    Localização
                  </h3>
                  <p className="text-[#5C5C5C] font-['Inter'] text-sm">
                    Lisboa, Portugal
                  </p>
                </div>

                <div className="bg-white p-6 rounded-sm border border-[#D6D2C4]">
                  <div className="w-12 h-12 rounded-full bg-[#0F5A34]/10 flex items-center justify-center mb-4">
                    <Phone size={24} className="text-[#0F5A34]" />
                  </div>
                  <h3 className="font-['Montserrat'] text-sm font-semibold uppercase tracking-wider text-[#1A1A1A] mb-2">
                    Redes Sociais
                  </h3>
                  <p className="text-[#5C5C5C] font-['Inter'] text-sm">
                    @portugalavista
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-8">
              <div className="bg-white p-8 rounded-sm border border-[#D6D2C4]">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-6">
                  Envie sua Mensagem
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-['Montserrat'] text-sm font-medium">
                        Nome
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Seu nome"
                        required
                        className="border-[#D6D2C4]"
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-['Montserrat'] text-sm font-medium">
                        Email
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="seu@email.com"
                        required
                        className="border-[#D6D2C4]"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="font-['Montserrat'] text-sm font-medium">
                      Assunto
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Assunto da mensagem"
                      required
                      className="border-[#D6D2C4]"
                      data-testid="contact-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-['Montserrat'] text-sm font-medium">
                      Mensagem
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Escreva sua mensagem..."
                      required
                      rows={6}
                      className="border-[#D6D2C4] resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full md:w-auto bg-[#0F5A34] hover:bg-[#0A3F24] font-['Montserrat'] text-sm px-8"
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <Loader2 size={16} className="animate-spin mr-2" />
                    ) : (
                      <Send size={16} className="mr-2" />
                    )}
                    Enviar Mensagem
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
